import {
  KeywordItem,
  NegativeKeyword,
  PropertySeoItem,
  LocalityHubSeoItem,
  PortalHomepageSeoItem,
  GoogleAdGroup,
  DirectChipKey,
  QueryPerformanceStatus,
  SeoPendingApprovalItem,
  SeoEngineConfig,
} from '../types/seo';
import { CmsProject, CmsSiteSettings } from '../types/cms';

// ─── Deterministic Character Clamping ──────────────────────────────────────────

export function clampSeoTitle(title: string, maxChars = 60): string {
  const trimmed = title.trim().replace(/\s+/g, ' ');
  if (trimmed.length <= maxChars) return trimmed;
  // Try slicing at word boundary
  const sliced = trimmed.slice(0, maxChars);
  const lastSpace = sliced.lastIndexOf(' ');
  const lastPipe = sliced.lastIndexOf('|');
  const cutIndex = Math.max(lastSpace, lastPipe);
  if (cutIndex > 40) {
    return sliced.slice(0, cutIndex).trim();
  }
  return sliced.trim();
}

export function clampMetaDescription(desc: string, minChars = 125, maxChars = 155): string {
  let cleaned = desc.trim().replace(/\s+/g, ' ');
  if (cleaned.length > maxChars) {
    // Cut off cleanly at the last sentence or word
    const sliced = cleaned.slice(0, maxChars);
    const lastPeriod = sliced.lastIndexOf('.');
    if (lastPeriod >= minChars) {
      return sliced.slice(0, lastPeriod + 1).trim();
    }
    const lastComma = sliced.lastIndexOf(',');
    const lastSpace = sliced.lastIndexOf(' ');
    const cut = lastComma > minChars ? lastComma : (lastSpace > minChars ? lastSpace : maxChars);
    return sliced.slice(0, cut).trim() + '...';
  }
  return cleaned;
}

// ─── SXO & SEO Audit Scoring ──────────────────────────────────────────────────

export function calculateSeoScore(params: {
  title: string;
  description: string;
  hasFaqs: boolean;
  hasSchema: boolean;
  hasRera: boolean;
  hasVastu: boolean;
  hasSuratMention: boolean;
  contentWordCount: number;
}): { score: number; checks: { label: string; passed: boolean; tip: string }[] } {
  const titleLen = params.title.length;
  const descLen = params.description.length;

  const checks = [
    {
      label: 'Page title entered',
      passed: titleLen > 0,
      tip: `Current: ${titleLen} characters. Check the live search appearance; Google may rewrite titles.`,
    },
    {
      label: 'Page description entered',
      passed: descLen > 0,
      tip: `Current: ${descLen} characters. Google may use page content instead of this text.`,
    },
    {
      label: 'Surat / Micro-market Keyword Presence',
      passed: params.hasSuratMention,
      tip: 'Includes hyper-local targeting for Surat, Gujarat buyers.',
    },
    {
      label: 'GujRERA Registration Verified',
      passed: params.hasRera,
      tip: 'RERA compliance boosts buyer trust and E-E-A-T score.',
    },
    {
      label: 'Schema.org JSON-LD Structured Data',
      passed: params.hasSchema,
      tip: 'Structured data should match visible content and be validated on the public page.',
    },
    {
      label: 'Buyer FAQs included',
      passed: params.hasFaqs,
      tip: 'Useful answers help buyers; FAQ rich results are no longer available in Google Search.',
    },
    {
      label: 'Page body entered',
      passed: params.contentWordCount > 0,
      tip: `Current: ~${params.contentWordCount} words. Check whether the content answers real buyer questions; word count alone is not a ranking target.`,
    },
  ];

  const passedCount = checks.filter((c) => c.passed).length;
  const score = Math.round((passedCount / checks.length) * 100);

  return { score, checks };
}

export function calculateQueryPerformanceStatus(kw: {
  avgPosition: number;
  ctr: number;
  positionDelta?: number;
  impressions: number;
  isExcluded?: boolean;
}): QueryPerformanceStatus {
  if (kw.isExcluded) return 'underperforming';
  if (kw.avgPosition <= 3.0 && kw.ctr >= 5.0) return 'top_performer';
  if (kw.positionDelta && kw.positionDelta >= 1.5) return 'rising_star';
  if (kw.impressions >= 10000 && kw.ctr < 2.0) return 'low_ctr_alert';
  if (kw.avgPosition > 10.0 || (kw.positionDelta && kw.positionDelta < -1.0)) return 'underperforming';
  if (kw.avgPosition <= 5.0) return 'top_performer';
  return 'rising_star';
}

// ─── Initial Seed Intelligence for Surat & Swarna Projects ───────────────────

export const INITIAL_KEYWORDS: KeywordItem[] = [
  {
    id: 'kw-1',
    query: 'ready possession 2 bhk flat in jahangirpura surat',
    monthlySearches: 4800,
    impressions: 21400,
    clicks: 1560,
    ctr: 7.29,
    avgPosition: 2.3,
    positionDelta: 3.4, // Climbed 3.4 ranks in past 30 days
    clicksDeltaPercent: 28.4,
    performanceStatus: 'top_performer',
    history: [
      { date: '2026-09-01', position: 5.7, impressions: 4800, clicks: 290 },
      { date: '2026-09-08', position: 4.2, impressions: 5100, clicks: 360 },
      { date: '2026-09-15', position: 3.1, impressions: 5500, clicks: 420 },
      { date: '2026-09-22', position: 2.3, impressions: 6000, clicks: 490 },
    ],
    intent: 'High Commercial',
    microMarket: 'Jahangirpura',
    typology: '2 BHK Flat',
    budgetTier: 'Contact for Pricing',
    possessionTarget: 'Ready to Move',
    isExcluded: false,
    suggestedAdGroup: 'Surat - Swarna Lagom Ready 2 BHK (Vanakla / Jahangirpura)',
  },
  {
    id: 'kw-2',
    query: '2 bhk flats in vanakla surat near dandi road',
    monthlySearches: 3600,
    impressions: 14200,
    clicks: 1240,
    ctr: 8.73,
    avgPosition: 1.8,
    positionDelta: 2.6,
    clicksDeltaPercent: 34.2,
    performanceStatus: 'top_performer',
    history: [
      { date: '2026-09-01', position: 4.4, impressions: 3100, clicks: 240 },
      { date: '2026-09-08', position: 3.2, impressions: 3400, clicks: 290 },
      { date: '2026-09-15', position: 2.4, impressions: 3700, clicks: 340 },
      { date: '2026-09-22', position: 1.8, impressions: 4000, clicks: 370 },
    ],
    intent: 'High Commercial',
    microMarket: 'Vanakla',
    typology: '2 BHK Flat',
    budgetTier: 'Contact for Pricing',
    possessionTarget: 'Ready to Move',
    isExcluded: false,
    suggestedAdGroup: 'Surat - Swarna Lagom Ready 2 BHK (Vanakla / Jahangirpura)',
  },
  {
    id: 'kw-3',
    query: 'swarna avsar surat possession dec 2026 2 bhk',
    monthlySearches: 4100,
    impressions: 16800,
    clicks: 1390,
    ctr: 8.27,
    avgPosition: 1.4,
    positionDelta: 1.8,
    clicksDeltaPercent: 41.5,
    performanceStatus: 'top_performer',
    history: [
      { date: '2026-09-01', position: 3.2, impressions: 3600, clicks: 270 },
      { date: '2026-09-08', position: 2.5, impressions: 4100, clicks: 330 },
      { date: '2026-09-15', position: 1.9, impressions: 4400, clicks: 380 },
      { date: '2026-09-22', position: 1.4, impressions: 4700, clicks: 410 },
    ],
    intent: 'High Commercial',
    microMarket: 'Vanakla',
    typology: '2 BHK Flat',
    budgetTier: 'Contact for Pricing',
    possessionTarget: 'Dec 2026 Handover',
    isExcluded: false,
    suggestedAdGroup: 'Surat - Swarna Avsar Dec 2026 2 BHK (Vanakla / Palanpur Canal)',
  },
  {
    id: 'kw-4',
    query: 'flats near palanpur canal road surat under construction',
    monthlySearches: 3900,
    impressions: 15300,
    clicks: 1120,
    ctr: 7.32,
    avgPosition: 2.7,
    positionDelta: 4.1, // Rising star
    clicksDeltaPercent: 52.0,
    performanceStatus: 'rising_star',
    history: [
      { date: '2026-09-01', position: 6.8, impressions: 2900, clicks: 180 },
      { date: '2026-09-08', position: 5.1, impressions: 3700, clicks: 260 },
      { date: '2026-09-15', position: 3.8, impressions: 4100, clicks: 320 },
      { date: '2026-09-22', position: 2.7, impressions: 4600, clicks: 360 },
    ],
    intent: 'High Commercial',
    microMarket: 'Palanpur Canal',
    typology: '2 BHK Flat',
    budgetTier: 'Contact for Pricing',
    possessionTarget: 'Dec 2026 Handover',
    isExcluded: false,
    suggestedAdGroup: 'Surat - Swarna Avsar Dec 2026 2 BHK (Vanakla / Palanpur Canal)',
  },
  {
    id: 'kw-5',
    query: 'swarna lagom ready possession 2bhk vanakla',
    monthlySearches: 3100,
    impressions: 11900,
    clicks: 1410,
    ctr: 11.85,
    avgPosition: 1.1,
    positionDelta: 0.8,
    clicksDeltaPercent: 19.5,
    performanceStatus: 'top_performer',
    history: [
      { date: '2026-09-01', position: 1.9, impressions: 2600, clicks: 310 },
      { date: '2026-09-08', position: 1.5, impressions: 2900, clicks: 340 },
      { date: '2026-09-15', position: 1.3, impressions: 3100, clicks: 370 },
      { date: '2026-09-22', position: 1.1, impressions: 3300, clicks: 390 },
    ],
    intent: 'Navigational',
    microMarket: 'Vanakla',
    typology: '2 BHK Flat',
    budgetTier: 'Contact for Pricing',
    possessionTarget: 'Ready to Move',
    isExcluded: false,
    suggestedAdGroup: 'Swarna Lagom - Ready Brand Hub',
  },
  {
    id: 'kw-6',
    query: 'new residential projects in jahangirabad surat 2 bhk',
    monthlySearches: 3400,
    impressions: 13600,
    clicks: 980,
    ctr: 7.21,
    avgPosition: 2.9,
    positionDelta: 2.2,
    clicksDeltaPercent: 24.0,
    performanceStatus: 'rising_star',
    history: [
      { date: '2026-09-01', position: 5.1, impressions: 2900, clicks: 190 },
      { date: '2026-09-08', position: 4.3, impressions: 3300, clicks: 230 },
      { date: '2026-09-15', position: 3.5, impressions: 3600, clicks: 260 },
      { date: '2026-09-22', position: 2.9, impressions: 3800, clicks: 300 },
    ],
    intent: 'High Commercial',
    microMarket: 'Jahangirabad',
    typology: '2 BHK Flat',
    budgetTier: 'Contact for Pricing',
    possessionTarget: 'Dec 2026 Handover',
    isExcluded: false,
    suggestedAdGroup: 'Jahangirabad & Palanpur - 2 BHK High Intent',
  },
  {
    id: 'kw-7',
    query: 'commercial shops on dandi road jahangirpura surat ready possession',
    monthlySearches: 2800,
    impressions: 9800,
    clicks: 650,
    ctr: 6.63,
    avgPosition: 3.1,
    positionDelta: 1.7,
    clicksDeltaPercent: 18.0,
    performanceStatus: 'top_performer',
    history: [
      { date: '2026-09-01', position: 4.8, impressions: 2100, clicks: 130 },
      { date: '2026-09-08', position: 4.0, impressions: 2400, clicks: 150 },
      { date: '2026-09-15', position: 3.6, impressions: 2600, clicks: 170 },
      { date: '2026-09-22', position: 3.1, impressions: 2700, clicks: 200 },
    ],
    intent: 'Transactional',
    microMarket: 'Dandi Road',
    typology: 'Commercial Retail Shop',
    budgetTier: 'Contact for Pricing',
    possessionTarget: 'Ready to Move',
    isExcluded: false,
    suggestedAdGroup: 'Commercial Shops - Vanakla / Dandi Road Surat',
  },
  {
    id: 'kw-8',
    query: '1 bhk room for rent in jahangirpura jahangirabad pg',
    monthlySearches: 4900,
    impressions: 17600,
    clicks: 110,
    ctr: 0.62,
    avgPosition: 15.4,
    positionDelta: -2.1,
    clicksDeltaPercent: -35.0,
    performanceStatus: 'low_ctr_alert',
    history: [
      { date: '2026-09-01', position: 13.3, impressions: 4100, clicks: 35 },
      { date: '2026-09-08', position: 14.1, impressions: 4400, clicks: 28 },
      { date: '2026-09-15', position: 14.9, impressions: 4500, clicks: 25 },
      { date: '2026-09-22', position: 15.4, impressions: 4600, clicks: 22 },
    ],
    intent: 'Investigational',
    microMarket: 'Jahangirpura',
    typology: 'General',
    budgetTier: 'Contact for Pricing',
    possessionTarget: 'Any',
    isExcluded: true,
    exclusionReason: 'Rental/PG negative keyword (Buyers only)',
  },
  {
    id: 'kw-9',
    query: 'dandi road farm house party plot rental rate',
    monthlySearches: 3100,
    impressions: 9500,
    clicks: 35,
    ctr: 0.37,
    avgPosition: 18.2,
    positionDelta: -3.0,
    clicksDeltaPercent: -45.0,
    performanceStatus: 'underperforming',
    history: [
      { date: '2026-09-01', position: 15.2, impressions: 2100, clicks: 12 },
      { date: '2026-09-08', position: 16.5, impressions: 2300, clicks: 10 },
      { date: '2026-09-15', position: 17.4, impressions: 2500, clicks: 8 },
      { date: '2026-09-22', position: 18.2, impressions: 2600, clicks: 5 },
    ],
    intent: 'Investigational',
    microMarket: 'Dandi Road',
    typology: 'General',
    budgetTier: 'Contact for Pricing',
    possessionTarget: 'Any',
    isExcluded: true,
    exclusionReason: 'Non-residential party plot rental query',
  },
];

export const INITIAL_NEGATIVES: NegativeKeyword[] = [
  { id: 'neg-1', term: 'rent', category: 'Rental / PG', stem: 'rent', matchType: 'phrase', isPermanent: true, addedAt: '2026-09-01' },
  { id: 'neg-2', term: 'rental', category: 'Rental / PG', stem: 'rental', matchType: 'phrase', isPermanent: true, addedAt: '2026-09-01' },
  { id: 'neg-3', term: 'pg hostel', category: 'Rental / PG', stem: 'pg', matchType: 'broad', isPermanent: true, addedAt: '2026-09-01' },
  { id: 'neg-4', term: 'room sharing', category: 'Cheap Lodge / Sharing', stem: 'sharing', matchType: 'phrase', isPermanent: true, addedAt: '2026-09-01' },
  { id: 'neg-5', term: 'cheap lodge', category: 'Cheap Lodge / Sharing', stem: 'lodge', matchType: 'broad', isPermanent: true, addedAt: '2026-09-01' },
  { id: 'neg-6', term: 'party plot rent', category: 'Event / Party Plot', stem: 'party plot', matchType: 'broad', isPermanent: true, addedAt: '2026-09-01' },
  { id: 'neg-7', term: 'dispute case', category: 'Legal Dispute', stem: 'dispute', matchType: 'broad', isPermanent: true, addedAt: '2026-09-01' },
  { id: 'neg-8', term: 'demolition notice', category: 'Demolition', stem: 'demolition', matchType: 'phrase', isPermanent: true, addedAt: '2026-09-01' },
  { id: 'neg-9', term: 'used furniture olx', category: 'Used Furniture', stem: 'furniture', matchType: 'broad', isPermanent: true, addedAt: '2026-09-01' },
];

export const INITIAL_PROPERTY_LISTINGS: PropertySeoItem[] = [
  {
    id: 'prop-1',
    projectKey: 'swarna-lagom',
    projectName: 'Swarna Lagom',
    unitTypology: '2 BHK Flat',
    carpetAreaSqFt: 885,
    superBuiltUpSqFt: 1240,
    floorRange: '3rd to 11th Floor',
    vastuFacing: 'East',
    priceInr: 0,
    pricePerSqFt: 0,
    possessionStage: 'Ready Possession',
    possessionDate: 'Ready Possession (Immediate)',
    gujReraNumber: 'PR/GJ/SURAT/SURAT-CITY/SUDA/MAA09876/010122',
    microMarket: 'Vanakla',
    city: 'Surat',
    seoTitle: '2 BHK Ready Flat in Vanakla Surat | Swarna Lagom (Zero GST)',
    metaDescription: 'Buy ready possession 2 BHK in Swarna Lagom, Vanakla Surat near Jahangirpura & Dandi Road. 885 sq.ft carpet, 100% Vastu East facing, GujRERA approved.',
    highlightsHtml: `
      <div class="property-highlights">
        <h4>✨ Unit Highlights & Luxury Specifications</h4>
        <ul>
          <li><strong>Prime Location:</strong> Vanakla, Surat — 2 mins from Jahangirpura & Dandi Road Corridor</li>
          <li><strong>Carpet Area:</strong> 885 sq.ft. (Super Built-up: 1,240 sq.ft.)</li>
          <li><strong>Vastu Compliance:</strong> 100% Vastu Compliant with auspicious East entrance</li>
          <li><strong>Possession Status:</strong> Ready to Move / Immediate OC Received (Zero GST)</li>
          <li><strong>Car Parking:</strong> 1 Covered Reserved Stilt Parking + Visitor Parking</li>
          <li><strong>Elevators & Power:</strong> Dual High-speed Elevators with 100% DG backup for common areas</li>
          <li><strong>Sanitary & Flooring:</strong> Glazed vitrified tiles with Jaquar fittings</li>
        </ul>
      </div>
    `,
    faqs: [
      {
        question: 'What is the location advantage of Swarna Lagom in Vanakla Surat?',
        answer: 'Swarna Lagom is situated in Vanakla, offering direct connectivity to Jahangirpura, Dandi Road, Palanpur Canal Road, and Jahangirabad, with quick access to the city center and Rander.',
      },
      {
        question: 'What is the GujRERA registration number for Swarna Lagom?',
        answer: 'Swarna Lagom is officially registered under GujRERA with registration number PR/GJ/SURAT/SURAT-CITY/SUDA/MAA09876/010122.',
      },
      {
        question: 'Is Swarna Lagom ready for immediate possession in Surat?',
        answer: 'Yes, Swarna Lagom has received complete Building Completion (BCC) & Occupancy Certificates. You can move in immediately post registry with Zero GST.',
      },
      {
        question: 'Which banks offer approved home loans for Swarna Lagom?',
        answer: 'Pre-approved home loans are available with SBI, HDFC Bank, ICICI Bank, Bank of Baroda, and Axis Bank with attractive interest rates starting at 8.35% p.a.',
      },
    ],
    schemaJson: {
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      name: '2 BHK Ready Possession Flat in Swarna Lagom, Vanakla Surat',
      url: 'https://www.swarnagroup.in/projects/swarna-lagom/2-bhk',
      datePosted: '2026-09-01',
      description: 'Ready to move 2 BHK apartment in Swarna Lagom, Vanakla, Surat (near Jahangirpura & Dandi Road). GujRERA registered with premium amenities and zero GST.',
      category: 'Sale',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        validFrom: '2026-09-01',
        description: 'Contact developer for pricing',
      },
      about: {
        '@type': 'Apartment',
        name: 'Swarna Lagom 2 BHK Residence',
        numberOfRooms: 2,
        floorSize: {
          '@type': 'QuantitativeValue',
          value: 885,
          unitCode: 'FTK',
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Swarna Lagom, Vanakla',
          addressLocality: 'Vanakla, near Jahangirpura & Dandi Road',
          addressRegion: 'Surat, Gujarat',
          postalCode: '395005',
          addressCountry: 'IN',
        },
      },
    },
    auditScore: 100,
    syncStatus: 'synced',
    lastSyncedAt: '2026-09-24 18:30 IST',
    lastEditedAt: '2026-09-24 18:30 IST',
  },
  {
    id: 'prop-2',
    projectKey: 'swarna-avsar',
    projectName: 'Swarna Avsar (Dec 2026 Handover)',
    unitTypology: '2 BHK Flat',
    carpetAreaSqFt: 890,
    superBuiltUpSqFt: 1250,
    floorRange: '2nd to 14th Floor',
    vastuFacing: 'North-East',
    priceInr: 0,
    pricePerSqFt: 0,
    possessionStage: 'Under Construction (Possession: Dec 2026)',
    possessionDate: 'Possession: December 2026',
    gujReraNumber: 'PR/GJ/SURAT/SURAT-CITY/SUDA/RAA10245/150624',
    microMarket: 'Vanakla',
    city: 'Surat',
    seoTitle: '2 BHK Flats in Vanakla Surat | Swarna Avsar Dec 2026',
    metaDescription: 'Book 2 BHK flat in Swarna Avsar, Vanakla Surat near Palanpur Canal & Jahangirpura. Dec 2026 possession, 10:90 flexible payment plan, GujRERA certified.',
    highlightsHtml: `
      <div class="property-highlights">
        <h4>🏗️ Project & Typology Specifications</h4>
        <ul>
          <li><strong>Strategic Hub:</strong> Vanakla, Surat — close to Palanpur Canal Road & Jahangirabad</li>
          <li><strong>Carpet Area:</strong> 890 sq.ft. (Super Built-up: 1,250 sq.ft.)</li>
          <li><strong>Possession Timeline:</strong> Under Construction, on-schedule handover in <strong>December 2026</strong></li>
          <li><strong>Vastu Alignment:</strong> North-East entrance maximizing natural ventilation and daylight</li>
          <li><strong>Payment Schedule:</strong> Construction-linked flexible 10:90 milestone payment structure</li>
          <li><strong>Lifestyle Amenities:</strong> Rooftop sky garden, gymnasium, children play zone, EV charging points</li>
          <li><strong>GujRERA Certified:</strong> 100% clear title with SUDA sanctioned plans</li>
        </ul>
      </div>
    `,
    faqs: [
      {
        question: 'Where is Swarna Avsar located in Surat?',
        answer: 'Swarna Avsar is located in Vanakla, Surat, enjoying direct access to Palanpur Canal Road, Jahangirpura, Jahangirabad, and Dandi Road.',
      },
      {
        question: 'What is the possession date for Swarna Avsar?',
        answer: 'Swarna Avsar is actively under construction and scheduled for verified possession in December 2026 as registered with GujRERA.',
      },
      {
        question: 'What is the payment milestone plan available for Dec 2026 delivery?',
        answer: 'We offer an investor-friendly construction linked milestone plan (CLP) with 10% on booking and balanced payments linked strictly to slab completions.',
      },
    ],
    schemaJson: {
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      name: '2 BHK Smart Home in Swarna Avsar Vanakla Surat - Dec 2026 Possession',
      url: 'https://www.swarnagroup.in/projects/swarna-avsar/2-bhk',
      datePosted: '2026-09-10',
      description: 'Under construction 2 BHK flats in Vanakla, Surat near Palanpur Canal Road with December 2026 delivery. High rental ROI and GujRERA certified.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/PreOrder',
        description: 'Contact developer for pricing',
      },
      about: {
        '@type': 'Apartment',
        name: 'Swarna Avsar 2 BHK Residence',
        numberOfRooms: 2,
        floorSize: {
          '@type': 'QuantitativeValue',
          value: 890,
          unitCode: 'FTK',
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Swarna Avsar, Vanakla',
          addressLocality: 'Vanakla, near Palanpur Canal & Jahangirpura',
          addressRegion: 'Surat, Gujarat',
          postalCode: '395005',
          addressCountry: 'IN',
        },
      },
    },
    auditScore: 98,
    syncStatus: 'synced',
    lastSyncedAt: '2026-09-22 14:15 IST',
    lastEditedAt: '2026-09-22 14:15 IST',
  },
  {
    id: 'prop-3',
    projectKey: 'swarna-lagom',
    projectName: 'Swarna Lagom Commercial',
    unitTypology: 'Commercial Retail Shop',
    carpetAreaSqFt: 420,
    superBuiltUpSqFt: 630,
    floorRange: 'Ground Floor Road Facing',
    vastuFacing: 'East',
    priceInr: 0,
    pricePerSqFt: 0,
    possessionStage: 'Ready Possession',
    possessionDate: 'Ready Possession (Immediate)',
    gujReraNumber: 'PR/GJ/SURAT/SURAT-CITY/SUDA/MAA09876/010122',
    microMarket: 'Vanakla',
    city: 'Surat',
    seoTitle: 'Shops in Vanakla Surat | Ready Possession Swarna Lagom',
    metaDescription: 'High footfall ground floor commercial retail shops in Swarna Lagom Vanakla Surat near Dandi Road. 420 sq.ft carpet, 18ft frontage, GujRERA approved.',
    highlightsHtml: `
      <div class="property-highlights">
        <h4>🏢 Commercial Retail Specifications</h4>
        <ul>
          <li><strong>Carpet Area:</strong> 420 sq.ft. (18 ft Wide Road Frontage)</li>
          <li><strong>Location:</strong> Vanakla, Main Corridor connecting Jahangirpura to Dandi Road</li>
          <li><strong>Possession:</strong> Ready to move in / Immediate rental commencement potential</li>
          <li><strong>High Footfall:</strong> Surrounded by 3,500+ premium residential families in Vanakla-Jahangirpura</li>
          <li><strong>Provisions:</strong> Separate electrical sub-meter, rolling shutter, dedicated signage space</li>
        </ul>
      </div>
    `,
    faqs: [
      {
        question: 'What is the expected rental yield for commercial shops in Vanakla Surat?',
        answer: 'Commercial ground floor retail spaces on the Vanakla - Dandi Road corridor typically command rental yields of 6.5% to 8.5% per annum due to rapid residential expansion.',
      },
      {
        question: 'Is parking available for retail customers?',
        answer: 'Yes, Swarna Lagom features dedicated open customer parking on the ground floor frontage.',
      },
    ],
    schemaJson: {
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      name: 'Commercial Shop in Swarna Lagom, Vanakla Surat',
      url: 'https://www.swarnagroup.in/projects/swarna-lagom/commercial-shops',
      offers: {
        '@type': 'Offer',
        description: 'Contact developer for pricing',
      },
    },
    auditScore: 96,
    syncStatus: 'draft',
    lastEditedAt: '2026-09-25 10:20 IST',
  },
];

export const INITIAL_LOCALITY_HUBS: LocalityHubSeoItem[] = [
  {
    id: 'loc-1',
    slug: 'flats-in-vanakla-jahangirpura-surat',
    title: 'Flats & Apartments in Vanakla & Jahangirpura, Surat',
    type: 'locality',
    microMarket: 'Vanakla',
    projectRef: 'swarna-lagom',
    possessionFocus: 'Ready Possession',
    activeListingsCount: 18,
    avgPriceSqFt: 0,
    annualAppreciationRate: '12.4% YoY',
    seoTitle: 'Flats in Vanakla Jahangirpura Surat | Ready 2 BHK Flats',
    metaDescription: 'Explore verified 2 BHK apartments in Vanakla & Jahangirpura, Surat by Swarna Group. Ready possession & GujRERA certified flats near Dandi Road.',
    richGuideHtml: `
      <div class="locality-guide">
        <h3>📍 Vanakla & Jahangirpura, Surat — The Fast-Growing North-West Living Hub</h3>
        <p>
          Vanakla and Jahangirpura form Surat's most rapidly appreciating residential corridor, combining peaceful suburban greenery with high-speed road connectivity. Connected seamlessly via <strong>Dandi Road</strong> and <strong>Palanpur Canal Road</strong>, residents enjoy quick access to Rander, Adajan, and the city center.
        </p>
        <p>
          <strong>Social Infrastructure & Daily Conveniences:</strong> The micro-market is surrounded by reputed CBSE schools, local multi-specialty healthcare centers, vibrant grocery markets, and upcoming SUDA infrastructure projects.
        </p>
        <p>
          <strong>Real Estate Growth:</strong> Property appreciation along the Vanakla-Jahangirpura-Dandi Road belt has recorded strong double-digit growth. Swarna Group's flagship development, <strong>Swarna Lagom</strong>, provides ready-possession 2 BHK flats with complete BCC clearance and Zero GST.
        </p>
      </div>
    `,
    faqs: [
      {
        question: 'Why is Vanakla near Jahangirpura a top choice for 2 BHK buyers in Surat?',
        answer: 'Vanakla offers excellent connectivity, wide uncongested roads, proximity to Jahangirpura and Dandi Road, and rapid SUDA infrastructure development.',
      },
      {
        question: 'Are there ready-to-move GujRERA approved projects in Vanakla Surat?',
        answer: 'Yes, Swarna Lagom by Swarna Group provides 100% ready-to-move 2 BHK apartments with full BCC and GujRERA registration.',
      },
    ],
    schemaJson: {
      '@context': 'https://schema.org',
      '@type': 'Place',
      name: 'Vanakla Jahangirpura Surat Real Estate Hub',
      description: 'High-growth residential micro-market in Surat with direct Dandi Road and Palanpur Canal connectivity.',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Vanakla',
        addressRegion: 'Surat, Gujarat',
        addressCountry: 'IN',
      },
    },
    auditScore: 100,
    syncStatus: 'synced',
    lastSyncedAt: '2026-09-24 12:00 IST',
    lastEditedAt: '2026-09-24 12:00 IST',
  },
  {
    id: 'loc-2',
    slug: 'flats-near-palanpur-canal-jahangirabad-surat',
    title: 'Flats near Palanpur Canal & Jahangirabad, Surat (Dec 2026 Handover)',
    type: 'locality',
    microMarket: 'Palanpur Canal',
    projectRef: 'swarna-avsar',
    possessionFocus: 'Under Construction (Possession: Dec 2026)',
    activeListingsCount: 14,
    avgPriceSqFt: 0,
    annualAppreciationRate: '10.8% YoY',
    seoTitle: 'Flats near Palanpur Canal Surat | Swarna Avsar Dec 2026',
    metaDescription: 'Find new 2 BHK flats near Palanpur Canal & Jahangirabad Surat. Dec 2026 possession with flexible 10:90 payment plans, GujRERA certified.',
    richGuideHtml: `
      <div class="locality-guide">
        <h3>📍 Palanpur Canal Road & Jahangirabad — Prime Connectivity Corridor</h3>
        <p>
          The Palanpur Canal Road and Jahangirabad belt is the vital arterial connector between Pal/Adajan and North Surat. Wide DP roads, clean canalside walkways, and modern town planning make it ideal for young nuclear families.
        </p>
        <p>
          <strong>Dec 2026 Possession Hub:</strong> <strong>Swarna Avsar</strong> in Vanakla (minutes from Palanpur Canal & Jahangirabad) offers smart 2 BHK layouts, premium rooftop amenities, and flexible 10:90 construction-linked payment milestones with GujRERA safety.
        </p>
      </div>
    `,
    faqs: [
      {
        question: 'What are the upcoming projects near Palanpur Canal Road with Dec 2026 possession?',
        answer: 'Swarna Avsar offers smart 2 BHK configurations under construction with verified GujRERA handover in December 2026.',
      },
      {
        question: 'What is the connectivity advantage of Palanpur Canal Road?',
        answer: 'It provides direct signal-free transit to Pal, Adajan, Jahangirpura, and Dandi Road.',
      },
    ],
    schemaJson: {
      '@context': 'https://schema.org',
      '@type': 'Place',
      name: 'Palanpur Canal Jahangirabad Surat Real Estate Corridor',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Palanpur Canal Road',
        addressRegion: 'Surat, Gujarat',
        addressCountry: 'IN',
      },
    },
    auditScore: 98,
    syncStatus: 'synced',
    lastSyncedAt: '2026-09-23 16:40 IST',
    lastEditedAt: '2026-09-23 16:40 IST',
  },
  {
    id: 'loc-3',
    slug: 'swarna-lagom-project-hub',
    title: 'Swarna Lagom — Ready Possession 2 BHK Residential Hub (Vanakla)',
    type: 'project_hub',
    microMarket: 'Vanakla',
    projectRef: 'swarna-lagom',
    possessionFocus: 'Ready Possession',
    activeListingsCount: 12,
    avgPriceSqFt: 0,
    annualAppreciationRate: '12.1% YoY',
    seoTitle: 'Swarna Lagom Vanakla Surat | Ready 2 BHK Flats & Shops',
    metaDescription: 'Official portal for Swarna Lagom in Vanakla Surat near Jahangirpura. Ready possession 2 BHK flats & shops. GujRERA approved with bank loan tie-ups. Zero GST.',
    richGuideHtml: `
      <div class="locality-guide">
        <h3>🏛️ Swarna Lagom Project Hub — Ready to Move Elegance in Vanakla, Surat</h3>
        <p>
          Swarna Lagom is a landmark ready-to-move 2 BHK residential and commercial development by Swarna Group in Vanakla (near Jahangirpura & Dandi Road). With 100% completed construction and Occupancy Certificate (OC) received, buyers enjoy zero GST on ready flats and immediate handover.
        </p>
        <p>
          <strong>Architectural Highlights:</strong> Designed with meticulous Vastu principles, all units feature East or North facing main entrances, maximum natural cross-ventilation, glazed vitrified flooring, and earthquake-resistant RCC structure.
        </p>
      </div>
    `,
    faqs: [
      {
        question: 'Is GST applicable on buying a flat in Swarna Lagom?',
        answer: 'No GST is applicable on ready-to-move flats in Swarna Lagom because the project has received its official Building Completion Certificate (BCC).',
      },
      {
        question: 'How to schedule a site visit for Swarna Lagom in Vanakla?',
        answer: 'You can book an instant sample flat site visit online at www.swarnagroup.in or call Swarna Group sales desk directly with zero brokerage.',
      },
    ],
    schemaJson: {
      '@context': 'https://schema.org',
      '@type': 'ApartmentComplex',
      name: 'Swarna Lagom',
      url: 'https://www.swarnagroup.in/projects/swarna-lagom',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Swarna Lagom, Vanakla',
        addressLocality: 'Vanakla, near Jahangirpura',
        addressRegion: 'Surat, Gujarat',
        addressCountry: 'IN',
      },
    },
    auditScore: 100,
    syncStatus: 'synced',
    lastSyncedAt: '2026-09-24 15:10 IST',
    lastEditedAt: '2026-09-24 15:10 IST',
  },
  {
    id: 'loc-4',
    slug: 'swarna-avsar-project-hub',
    title: 'Swarna Avsar — Dec 2026 Handover Hub (Vanakla / Palanpur Canal)',
    type: 'project_hub',
    microMarket: 'Vanakla',
    projectRef: 'swarna-avsar',
    possessionFocus: 'Under Construction (Possession: Dec 2026)',
    activeListingsCount: 16,
    avgPriceSqFt: 0,
    annualAppreciationRate: '10.5% YoY',
    seoTitle: 'Swarna Avsar Surat | 2 BHK Flats Dec 2026 Possession',
    metaDescription: 'Official portal for Swarna Avsar in Surat. Under construction 2 BHK smart residences with confirmed December 2026 handover. Flexible construction-linked plan.',
    richGuideHtml: `
      <div class="locality-guide">
        <h3>🏛️ Swarna Avsar Project Hub — Future-Ready 2 BHK Homes in Surat</h3>
        <p>
          Swarna Avsar is an upcoming premier residential development by Swarna Group featuring thoughtfully designed 2 BHK apartments scheduled for delivery in <strong>December 2026</strong>. Built to contemporary architectural benchmarks with extensive lifestyle amenities.
        </p>
        <p>
          <strong>Investment Safeguards:</strong> Fully registered with GujRERA, payments are structured around transparent milestone completion stages with pre-approved banking consortium loans.
        </p>
      </div>
    `,
    faqs: [
      {
        question: 'What is the construction status of Swarna Avsar?',
        answer: 'Construction is progressing actively on schedule with target completion and handover in December 2026.',
      },
      {
        question: 'What payment plans are available for Swarna Avsar?',
        answer: 'A flexible construction-linked 10:90 milestone payment plan is available for buyers booking early units.',
      },
    ],
    schemaJson: {
      '@context': 'https://schema.org',
      '@type': 'ApartmentComplex',
      name: 'Swarna Avsar',
      url: 'https://www.swarnagroup.in/projects/swarna-avsar',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Pal',
        addressRegion: 'Surat, Gujarat',
        addressCountry: 'IN',
      },
    },
    auditScore: 100,
    syncStatus: 'synced',
    lastSyncedAt: '2026-09-24 15:10 IST',
    lastEditedAt: '2026-09-24 15:10 IST',
  },
];

export const INITIAL_HOMEPAGE_SEO: PortalHomepageSeoItem = {
  id: 'home-1',
  portalUrl: 'https://www.swarnagroup.in',
  brandName: 'Swarna Group',
  targetCity: 'Surat, Gujarat',
  gujReraRegisteredAuthority: 'GujRERA Certified Developer Network',
  seoTitle: 'Swarna Group | Verified Real Estate & Flats in Surat, Gujarat',
  metaDescription: 'Swarna Group is Surat\'s trusted builder for GujRERA certified ready possession & Dec 2026 flats, luxury apartments & shops. Direct builder booking, zero brokerage.',
  brandStoryHtml: `
    <div class="brand-authority-story">
      <h3>🏛️ Swarna Group — Engineering Trust & Architectural Excellence in Surat</h3>
      <p>
        Founded on the pillars of transparency, uncompromising structural quality, and on-time delivery, <strong>Swarna Group</strong> has emerged as one of Surat's premier real estate development firms. We bridge modern engineering with customer-centric design across Gujarat's fastest-growing commercial and residential corridors.
      </p>
      <p>
        <strong>Portfolio Diversity in Surat:</strong> Our current developments address key home buyer timelines: 
        <em>1) Ready Possession:</em> <strong>Swarna Lagom</strong> offers instant occupancy, 2 BHK ready flats, 100% BCC clearance, and zero GST liability for immediate move-ins.
        <em>2) High-Growth Pipeline:</em> <strong>Swarna Avsar</strong> caters to forward-looking families with confirmed delivery in <strong>December 2026</strong> backed by milestone-based construction payment safeguards.
      </p>
      <p>
        <strong>100% GujRERA & Direct Developer Dealing:</strong> Every unit listed on <em>www.swarnagroup.in</em> is directly marketed by the developer with zero brokerage fees, clear legal title opinions, and end-to-end home loan assistance with leading national banks.
      </p>
    </div>
  `,
  faqs: [
    {
      question: 'Why choose Swarna Group for buying property in Surat?',
      answer: 'Swarna Group offers 100% GujRERA verified projects, zero brokerage direct builder relationship, transparent legal documentation, and on-time delivery track record.',
    },
    {
      question: 'What projects are currently active on www.swarnagroup.in?',
      answer: 'We currently have two premier projects in Surat: 1) Ready Possession project (Swarna Lagom) with 2 BHK ready flats, and 2) Under Construction project (Swarna Avsar) with 2 BHK flats scheduled for handover in December 2026.',
    },
    {
      question: 'Do you assist with NRI real estate investments in Surat?',
      answer: 'Yes, our dedicated NRI Property Advisory desk assists global buyers with NRE/NRO account payments, virtual 3D walkthroughs, power of attorney facilitation, and high rental yield management in Surat.',
    },
    {
      question: 'How are bank home loans processed for Swarna properties?',
      answer: 'All our projects have approved project financial tie-ups (APF) with SBI, HDFC Bank, ICICI Bank, and Bank of Baroda for instant home loan sanctioning and subsidy guidance under PMAY.',
    },
  ],
  schemaJson: {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: 'Swarna Group',
    url: 'https://www.swarnagroup.in',
    logo: 'https://www.swarnagroup.in/logo.png',
    image: 'https://www.swarnagroup.in/brand-hero.jpg',
    telephone: '+91-98250-00000',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Swarna Corporate Hub, VIP Road, Vesu',
      addressLocality: 'Surat',
      addressRegion: 'Gujarat',
      postalCode: '395007',
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '21.1418',
      longitude: '72.7758',
    },
    sameAs: [
      'https://www.facebook.com/swarnagroup',
      'https://www.instagram.com/swarnagroup',
      'https://www.linkedin.com/company/swarna-group',
    ],
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://www.swarnagroup.in/search?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  },
  auditScore: 100,
  syncStatus: 'synced',
  lastSyncedAt: '2026-09-24 10:00 IST',
  lastEditedAt: '2026-09-24 10:00 IST',
};

// ─── Directives Re-generation Engine ──────────────────────────────────────────

export function generateRefinedSeoContent(params: {
  entityType: 'PROPERTY' | 'LOCALITY' | 'HOMEPAGE';
  title: string;
  description: string;
  directives: DirectChipKey[];
  customPrompt: string;
  projectName?: string;
  typology?: string;
  locality?: string;
  possessionStage?: string;
  carpetArea?: number;
  priceInr?: number;
  reraNumber?: string;
}): {
  refinedTitle: string;
  refinedDescription: string;
  refinedRichContent: string;
  refinedFaqs: { question: string; answer: string }[];
  appliedDirectives: string[];
} {
  const isReady = params.possessionStage?.toLowerCase().includes('ready') || params.directives.includes('rera_ready');
  const locality = params.locality || 'Vesu';
  const project = params.projectName || (isReady ? 'Swarna Lagom' : 'Swarna Avsar');
  const typology = params.typology || '2 BHK Flat';
  const carpet = params.carpetArea || 885;

  let title = '';
  let desc = '';
  let richHtml = '';
  const faqs: { question: string; answer: string }[] = [];
  const applied: string[] = [];

  // Generate Title <= 60 chars
  if (params.entityType === 'PROPERTY') {
    if (isReady) {
      title = `${typology} in ${project} ${locality}, Surat | Ready Possession`;
    } else {
      title = `${typology} in ${locality} Surat | ${project} Dec 2026 Handover`;
    }
  } else if (params.entityType === 'LOCALITY') {
    if (isReady) {
      title = `Flats for Sale in ${locality} Surat | Ready 2 BHK Swarna Lagom`;
    } else {
      title = `Flats in ${locality} Surat | Dec 2026 Swarna Avsar`;
    }
  } else {
    title = `Swarna Group | Verified Real Estate & Flats in Surat, Gujarat`;
  }
  title = clampSeoTitle(title, 60);

  // Generate Description strictly 125-155 chars
  if (params.entityType === 'PROPERTY') {
    if (isReady) {
      desc = `Buy ready possession ${typology} in ${project}, ${locality} Surat. ${carpet} sq.ft carpet, GujRERA approved, 100% Vastu East facing, immediate move-in.`;
    } else {
      desc = `Book ${typology} in ${project}, ${locality} Surat with Dec 2026 possession. ${carpet} sq.ft carpet, 10:90 flexible payment plan, GujRERA verified.`;
    }
  } else if (params.entityType === 'LOCALITY') {
    desc = `Explore verified 2 BHK flats in ${locality}, Surat by Swarna Group. Ready possession in Swarna Lagom & Dec 2026 handover in Swarna Avsar. Direct builder deals.`;
  } else {
    desc = `Swarna Group is Surat's trusted builder for GujRERA certified ready possession & Dec 2026 flats, luxury apartments & shops. Direct builder relationship, zero brokerage.`;
  }

  // Handle specific directives
  if (params.directives.includes('metro_tech')) {
    applied.push('Proximity to Surat Metro, Ring Road & Diamond Bourse');
    faqs.push({
      question: `How close is this property in ${locality} to Surat Metro & Diamond Bourse?`,
      answer: `The property is situated just 5-8 minutes from Surat Metro station and under 15 minutes from the world's largest office complex, Surat Diamond Bourse (SDB Khajod).`,
    });
  }

  if (params.directives.includes('rental_roi')) {
    applied.push('High Rental Yield & Capital Appreciation in Surat');
    faqs.push({
      question: `What is the expected rental yield and capital appreciation in ${locality} Surat?`,
      answer: `Properties in ${locality} Surat enjoy an attractive rental yield and consistent annual capital appreciation driven by infrastructure expansions.`,
    });
  }

  if (params.directives.includes('price_loan')) {
    applied.push('Home Loan EMI & Bank Tie-up FAQs');
    faqs.push({
      question: `Which banks offer pre-approved home loans for ${project}?`,
      answer: `Pre-approved home loans are available with SBI, HDFC, ICICI, and Bank of Baroda with zero processing fee offers and attractive home loan rates.`,
    });
  }

  if (params.directives.includes('vastu_facing')) {
    applied.push('100% Vastu Compliant East/North Facing');
  }

  if (params.directives.includes('luxury_amenities')) {
    applied.push('Luxury Amenities (Clubhouse, Infinity Pool, Landscaped Garden)');
  }

  if (params.directives.includes('dec_2026_timeline')) {
    applied.push('Dec 2026 Handover & Construction Linked Plan');
    faqs.push({
      question: `What is the construction milestone schedule for December 2026 delivery?`,
      answer: `Construction is on schedule with foundation completed. Payments follow a transparent 10:90 construction-linked milestone plan backed by GujRERA escrow account.`,
    });
  }

  // Base FAQs
  faqs.push({
    question: `Is ${project} registered with GujRERA in Surat?`,
    answer: `Yes, ${project} is 100% compliant and registered with GujRERA authority under registration number ${params.reraNumber || 'PR/GJ/SURAT/SURAT-CITY/SUDA/MAA09876/010122'}.`,
  });

  richHtml = `
    <div class="refined-sxo-content">
      <h4>📍 ${project} — ${locality}, Surat Excellence</h4>
      <p>
        Designed with precision engineering and modern aesthetics, <strong>${project}</strong> delivers exceptional living standards in ${locality}, Surat. Featuring ${carpet} sq.ft. carpet layouts, 100% Vastu compliance, and premium community infrastructure.
      </p>
      <ul>
        <li><strong>Handover Timeline:</strong> ${isReady ? 'Immediate Ready Possession with Occupancy Certificate (Zero GST)' : 'On-schedule December 2026 delivery'}</li>
        <li><strong>RERA Verification:</strong> ${params.reraNumber || 'GujRERA Certified with 100% clear title'}</li>
        <li><strong>Location Advantage:</strong> Quick access to Surat International Airport, Surat Metro, and top schools.</li>
      </ul>
    </div>
  `;

  desc = clampMetaDescription(desc, 125, 155);

  return {
    refinedTitle: title,
    refinedDescription: desc,
    refinedRichContent: richHtml,
    refinedFaqs: faqs,
    appliedDirectives: applied,
  };
}

// ─── Google Ads Campaign Bridge ───────────────────────────────────────────────

export const INITIAL_GOOGLE_AD_GROUPS: GoogleAdGroup[] = [
  {
    id: 'adg-1',
    name: 'Surat - Swarna Lagom Ready 2 BHK (Vanakla / Jahangirpura)',
    microMarket: 'Vanakla',
    typology: '2 BHK Flat',
    targetKeywords: [
      '+ready +possession +2bhk +surat',
      '"ready possession 2 bhk flat in jahangirpura surat"',
      '"2 bhk flats in vanakla surat near dandi road"',
      '[ready to move flats in vanakla surat]',
      '"swarna lagom surat"',
      '+rera +approved +flats +surat',
    ],
    negativeKeywords: ['rent', 'pg hostel', 'room sharing', 'used furniture olx', 'party plot rent'],
    finalUrl: 'https://www.swarnagroup.in/projects/swarna-lagom',
    headlines: [
      '2 BHK Ready Flats Vanakla',
      'Swarna Lagom Surat',
      'Immediate Ready Possession',
      'Near Jahangirpura Surat',
      'Zero Brokerage Direct Deal',
      'GujRERA Approved Homes',
      '885 Sq.Ft Carpet 2 BHK',
      '100% Vastu East Facing',
      'Visit Actual Sample Flat',
      'SBI & HDFC Loan Approved',
      '2 Min to Dandi Road',
      'Zero GST on Ready Flats',
      'Pricing on Request',
      'Book Free Site Visit Today',
      'Direct Developer Deal',
    ],
    descriptions: [
      'Ready possession 2 BHK in Swarna Lagom Vanakla Surat near Jahangirpura. Zero GST liability. Book visit!',
      '885 sq.ft carpet, 100% Vastu East facing, premium fittings & parking. Immediate registry.',
      'Buy directly from builder with zero brokerage. Pre-approved SBI & HDFC home loans.',
      'Experience ready possession flats on Vanakla - Dandi Road corridor. Visit actual sample flat now.',
    ],
    calloutExtensions: [
      'Zero Brokerage',
      'GujRERA Approved',
      'Sample Flat Ready',
      'Immediate Possession',
      'Zero GST Liability',
      'SBI Home Loan Approved',
    ],
    sitelinks: [
      { title: 'Swarna Lagom Specs', desc1: '885 Sqft Carpet Floor Plan', desc2: 'Glazed Vitrified Tiles', url: 'https://www.swarnagroup.in/projects/swarna-lagom/specs' },
      { title: 'Download Brochure', desc1: 'Get Official PDF Brochure', desc2: 'GujRERA Sanctioned Layouts', url: 'https://www.swarnagroup.in/projects/swarna-lagom/brochure' },
      { title: 'Book Site Visit', desc1: 'Schedule Free Weekend Tour', desc2: 'Pickup & Drop Facility Available', url: 'https://www.swarnagroup.in/site-visit' },
      { title: 'Dec 2026 Projects', desc1: 'Explore Swarna Avsar Vanakla', desc2: 'Flexible 10:90 Milestone Plan', url: 'https://www.swarnagroup.in/projects/swarna-avsar' },
    ],
  },
  {
    id: 'adg-2',
    name: 'Surat - Swarna Avsar Dec 2026 2 BHK (Vanakla / Palanpur Canal)',
    microMarket: 'Vanakla',
    typology: '2 BHK Flat',
    targetKeywords: [
      '+under +construction +flats +surat',
      '"swarna avsar surat possession dec 2026 2 bhk"',
      '"flats near palanpur canal road surat under construction"',
      '"new residential projects in jahangirabad surat 2 bhk"',
      '[flats in surat dec 2026]',
      '+flexible +payment +plan +flats +surat',
    ],
    negativeKeywords: ['rent', 'pg hostel', 'demolition', 'room sharing', 'party plot rent'],
    finalUrl: 'https://www.swarnagroup.in/projects/swarna-avsar',
    headlines: [
      '2 BHK Flats in Surat',
      'Swarna Avsar Surat',
      'Dec 2026 Possession',
      'Near Palanpur Canal Road',
      '10:90 Flexible Builder Plan',
      'GujRERA Verified Project',
      'Pricing on Request',
      'North-East Vastu Facing',
      'Rooftop Sky Garden & Gym',
      'Close to Jahangirabad',
      'Direct Builder Relationship',
      'Zero Brokerage Charges',
      'High Appreciation Area',
      'Construction on Schedule',
      'Book with Only 10% Down',
    ],
    descriptions: [
      'Book smart 2 BHK in Swarna Avsar Vanakla Surat near Palanpur Canal. Dec 2026 possession with 10:90 plan.',
      'GujRERA registered residential tower with rooftop gym, sky lounge & EV charging points.',
      'Direct developer booking with zero brokerage fees. Swift access to Jahangirpura & Adajan.',
      'Invest in Surat fastest growing residential corridor. High rental demand & transparent milestones.',
    ],
    calloutExtensions: [
      'Dec 2026 Handover',
      '10:90 Payment Plan',
      'GujRERA Registered',
      'Zero Brokerage',
      'Near Canal Corridor',
      'Rooftop Sky Lounge',
    ],
    sitelinks: [
      { title: 'Payment Milestones', desc1: 'Construction-Linked Plan', desc2: 'Pay 10% on Booking', url: 'https://www.swarnagroup.in/projects/swarna-avsar/payment-plan' },
      { title: 'Locality Guide Surat', desc1: 'Schools, Hospitals & Roads', desc2: 'Vanakla Growth Corridor', url: 'https://www.swarnagroup.in/localities/vanakla' },
      { title: 'Ready Possession Flats', desc1: 'Explore Swarna Lagom Vanakla', desc2: 'Immediate Move-in Available', url: 'https://www.swarnagroup.in/projects/swarna-lagom' },
    ],
  },
];

// ─── Google Ads Editor CSV Exporter ───────────────────────────────────────────

export function exportGoogleAdsEditorCsv(adGroups: GoogleAdGroup[]): string {
  const headers = [
    'Campaign',
    'Ad Group',
    'Keyword',
    'Criterion Type',
    'Headline 1',
    'Headline 2',
    'Headline 3',
    'Headline 4',
    'Headline 5',
    'Description 1',
    'Description 2',
    'Final URL',
    'Path 1',
    'Path 2',
  ];

  const rows: string[] = [headers.join(',')];

  adGroups.forEach((group) => {
    const campaignName = `Search_Surat_RealEstate_${group.microMarket.replace(/\s+/g, '_')}`;

    // Add keywords
    group.targetKeywords.forEach((kw) => {
      let cleanKw = kw.trim();
      let matchType = 'Broad';
      if (cleanKw.startsWith('[') && cleanKw.endsWith(']')) {
        matchType = 'Exact';
        cleanKw = cleanKw.slice(1, -1);
      } else if (cleanKw.startsWith('"') && cleanKw.endsWith('"')) {
        matchType = 'Phrase';
        cleanKw = cleanKw.slice(1, -1);
      }
      rows.push(
        [
          `"${campaignName}"`,
          `"${group.name}"`,
          `"${cleanKw}"`,
          `"${matchType}"`,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ].join(',')
      );
    });

    // Add negative keywords
    group.negativeKeywords.forEach((neg) => {
      rows.push(
        [
          `"${campaignName}"`,
          `"${group.name}"`,
          `"${neg}"`,
          '"Negative Phrase"',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ].join(',')
      );
    });

    // Add Responsive Search Ad Row
    rows.push(
      [
        `"${campaignName}"`,
        `"${group.name}"`,
        '',
        '',
        `"${group.headlines[0] || ''}"`,
        `"${group.headlines[1] || ''}"`,
        `"${group.headlines[2] || ''}"`,
        `"${group.headlines[3] || ''}"`,
        `"${group.headlines[4] || ''}"`,
        `"${group.descriptions[0] || ''}"`,
        `"${group.descriptions[1] || ''}"`,
        `"${group.finalUrl}"`,
        '"Surat"',
        '"Flats"',
      ].join(',')
    );
  });

  return rows.join('\n');
}

// ─── Autonomous ("No Human Touch") SEO & Schema Generators ────────────────────────

/**
 * Automatically builds complete enterprise-grade SEO, Schema.org JSON-LD, and FAQs from live CMS Project data.
 */
export function generateAutonomousProjectSeo(project: CmsProject): PropertySeoItem {
  const isReady = project.status === 'ready-possession';
  const possessionLabel = isReady ? 'Ready Possession' : 'Possession: Dec 2026';
  const microMarket = project.microMarket || 'Vanakla';
  const reraClean = project.reraNumber ? ` | GujRERA: ${project.reraNumber.split('/').pop() || 'Verified'}` : '';

  // 1. High-CTR Deterministic Title (Strictly <= 60 chars)
  const rawTitle = `2 BHK Flats in ${microMarket}, Surat | ${project.name} (${isReady ? 'Ready Move' : 'Dec 2026'})`;
  const seoTitle = clampSeoTitle(rawTitle, 60);

  // 2. High-Intent Meta Description (Strictly 125-155 chars)
  const amenitiesPreview = (project.amenities || []).slice(0, 3).map((a) => a.label).join(', ');
  const rawDesc = `Buy premium 2 BHK flats at ${project.name}, ${microMarket}, Surat. ${possessionLabel}. ${amenitiesPreview ? amenitiesPreview + '.' : ''} GujRERA verified. Book a site visit today.`;
  const metaDescription = clampMetaDescription(rawDesc, 125, 155);

  // 3. Autonomous Schema.org JSON-LD (RealEstateListing + ApartmentComplex + AggregateOffer)
  const schemaJson = {
    '@context': 'https://schema.org',
    '@type': ['RealEstateListing', 'ApartmentComplex'],
    '@id': `https://www.swarnagroup.in/projects/${project.slug}#listing`,
    name: project.name,
    description: project.shortDescription || project.description,
    url: `https://www.swarnagroup.in/projects/${project.slug}`,
    image: project.heroImage ? `https://www.swarnagroup.in${project.heroImage.startsWith('/') ? '' : '/'}${project.heroImage}` : 'https://www.swarnagroup.in/og-default.jpg',
    address: {
      '@type': 'PostalAddress',
      streetAddress: project.location,
      addressLocality: project.city || 'Surat',
      addressRegion: 'Gujarat',
      postalCode: '395005',
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 21.2391,
      longitude: 72.7798,
    },
    amenityFeature: (project.amenities || []).map((a) => ({
      '@type': 'LocationFeatureSpecification',
      name: a.label,
      value: true,
    })),
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'INR',
      availability: isReady ? 'https://schema.org/InStock' : 'https://schema.org/PreOrder',
      validFrom: '2024-01-01',
      description: `Premium 2 BHK homes in ${microMarket}, Surat.`,
    },
    provider: {
      '@type': 'RealEstateAgent',
      name: 'Swarna Group',
      url: 'https://www.swarnagroup.in',
      telephone: '+919925026000',
    },
  };

  // 4. Autonomous FAQs targeting Google PAA ("People Also Ask")
  const faqs = (project.faqs && project.faqs.length > 0)
    ? project.faqs.map((f) => ({ question: f.question, answer: f.answer }))
    : [
        {
          question: `What is the location and address of ${project.name}?`,
          answer: `${project.name} is located at ${project.location}. Strategically positioned on Ugat Canal Road with seamless connectivity across west Surat.`,
        },
        {
          question: `What is the GujRERA registration number for ${project.name}?`,
          answer: project.reraNumber ? `${project.name} is registered under GujRERA with registration number: ${project.reraNumber}.` : 'RERA registration details can be verified with our sales team.',
        },
        {
          question: `What 2 BHK configurations and sizes are offered?`,
          answer: `${project.name} offers thoughtfully planned 2 BHK residences with high carpet area efficiency, natural lighting, and cross ventilation.`,
        },
        {
          question: `How can I schedule a site visit or download the brochure?`,
          answer: `You can schedule an exclusive site visit or download the official brochure directly at swarnagroup.in or by calling +91 99250 26000.`,
        },
      ];

  // 5. Calculate Autonomous SXO Score
  const { score: auditScore } = calculateSeoScore({
    title: seoTitle,
    description: metaDescription,
    hasFaqs: faqs.length > 0,
    hasSchema: true,
    hasRera: Boolean(project.reraNumber),
    hasVastu: true,
    hasSuratMention: true,
    contentWordCount: (project.description || '').split(/\s+/).length + 100,
  });

  return {
    id: `prop-${project.slug}`,
    projectKey: (project.slug === 'swarna-lagom' ? 'swarna-lagom' : 'swarna-avsar'),
    projectName: project.name,
    unitTypology: '2 BHK Flat',
    carpetAreaSqFt: 730,
    superBuiltUpSqFt: 1200,
    floorRange: `1 to ${project.totalFloors || 14}`,
    vastuFacing: 'East',
    priceInr: 0,
    pricePerSqFt: 0,
    possessionStage: isReady ? 'Ready Possession' : 'Under Construction (Possession: Dec 2026)',
    possessionDate: project.completionDate || (isReady ? 'Immediate' : 'Dec 2026'),
    gujReraNumber: project.reraNumber || '',
    microMarket: (microMarket as any) || 'Vanakla',
    city: 'Surat',
    seoTitle,
    metaDescription,
    highlightsHtml: (project.highlights || []).map((h) => `<li>${h}</li>`).join(''),
    faqs,
    schemaJson,
    auditScore,
    syncStatus: 'synced',
    lastEditedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST',
  };
}

/**
 * Creates a Pending Approval Item for Month 1 Supervised Mode
 */
export function createPendingReviewItem(params: {
  entityId: string;
  entityType: 'PROPERTY' | 'LOCALITY' | 'HOMEPAGE';
  entityName: string;
  targetSlug: string;
  currentTitle: string;
  currentDescription: string;
  proposedTitle: string;
  proposedDescription: string;
  proposedKeywords: string[];
  proposedFaqs: { question: string; answer: string }[];
  proposedSchemaJson: Record<string, any>;
  auditScore: number;
  triggerSource: 'cms_project_save' | 'gsc_low_ctr_fix' | 'trend_breakout' | 'manual_audit';
  reasons: string[];
}): SeoPendingApprovalItem {
  return {
    id: `rev-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    entityId: params.entityId,
    entityType: params.entityType,
    entityName: params.entityName,
    targetSlug: params.targetSlug,
    currentTitle: params.currentTitle,
    currentDescription: params.currentDescription,
    proposedTitle: params.proposedTitle,
    proposedDescription: params.proposedDescription,
    proposedKeywords: params.proposedKeywords,
    proposedFaqs: params.proposedFaqs,
    proposedSchemaJson: params.proposedSchemaJson,
    auditScore: params.auditScore,
    aiRationale: params.reasons,
    triggerSource: params.triggerSource,
    status: 'pending',
    createdAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST',
  };
}

